import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import classnames from 'classnames';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import Button from 'choerodon-ui/pro/lib/button';
import { FuncType } from 'choerodon-ui/pro/lib/button/enum';
import { useModal } from 'choerodon-ui/pro/lib/modal-provider/ModalProvider';
import { $l } from 'choerodon-ui/pro/lib/locale-context';
import { getActiveKeyByGroupKey, getDataAttr, getHeader, getLeft, getTop, isTransformSupported, isVertical, setTransform } from './utils';
import warning from '../_util/warning';
import Ripple from '../ripple';
import TabBarInner from './TabBarInner';
import EventManager from '../_util/EventManager';
import { TabsType } from './enum';
import Icon from '../icon';
import Menu from '../menu';
import MenuItem from '../menu/MenuItem';
import Badge from '../badge';
import TabsContext from './TabsContext';
import KeyCode from '../_util/KeyCode';
import { Size } from '../_util/enum';
import CustomizationSettings from './customization-settings';
import Count from './Count';
const TabBar = function TabBar(props) {
    const { scrollAnimated, className, style, inkBarStyle, extraContent, tabBarGutter, inkBarAnimated, type, onRemoveTab, ...restProps } = props;
    const { keyboard, customizable, prefixCls, activeKey, activeGroupKey, tabBarPosition, hideOnlyGroup = false, groupedPanelsMap, currentPanelMap, onTabClick, onPrevClick = noop, onNextClick = noop, changeActiveKey, } = useContext(TabsContext);
    const modal = useModal();
    const openCustomizationModal = useCallback(() => {
        if (customizable) {
            const modalProps = {
                drawer: true,
                size: Size.small,
                title: $l('Tabs', 'customization_settings'),
                children: React.createElement(CustomizationSettings, null),
                bodyStyle: {
                    overflow: 'hidden auto',
                    padding: 0,
                },
            };
            modalProps.okText = $l('Tabs', 'save');
            modal.open(modalProps);
        }
    }, [customizable, modal]);
    const getNextActiveKey = useCallback((next) => {
        const list = [];
        currentPanelMap.forEach((c, key) => {
            if (!c.disabled) {
                if (next) {
                    list.push(key);
                }
                else {
                    list.unshift(key);
                }
            }
        });
        const { length } = list;
        if (activeKey && length) {
            const i = list.indexOf(activeKey);
            const itemIndex = i === length - 1 ? 0 : i + 1;
            return list[itemIndex] || list[0];
        }
        return undefined;
    }, [activeKey, currentPanelMap]);
    const handleKeyDown = useCallback(e => {
        if (keyboard === false) {
            return noop;
        }
        const { keyCode } = e;
        if (keyCode === KeyCode.RIGHT || keyCode === KeyCode.DOWN) {
            e.preventDefault();
            const nextKey = getNextActiveKey(true);
            if (nextKey) {
                changeActiveKey(nextKey);
            }
        }
        else if (keyCode === KeyCode.LEFT || keyCode === KeyCode.UP) {
            e.preventDefault();
            const previousKey = getNextActiveKey(false);
            if (previousKey) {
                changeActiveKey(previousKey);
            }
        }
    }, [keyboard, changeActiveKey, getNextActiveKey]);
    const handleTabClick = useCallback((key) => {
        if (onTabClick) {
            onTabClick(key);
        }
        changeActiveKey(key);
    }, [changeActiveKey, onTabClick]);
    const handleGroupSelect = useCallback((param) => {
        const { key } = param;
        if (activeGroupKey !== key) {
            const newActiveKey = getActiveKeyByGroupKey(groupedPanelsMap, key);
            if (newActiveKey) {
                changeActiveKey(newActiveKey, true);
            }
        }
    }, [changeActiveKey, activeGroupKey, groupedPanelsMap]);
    const resizeEvent = useMemo(() => new EventManager(typeof window === 'undefined' ? undefined : window), []);
    const lastNextPrevShownRef = useRef();
    const offsetRef = useRef(0);
    const containerRef = useRef(null);
    const navWrapRef = useRef(null);
    const navRef = useRef(null);
    const rootRef = useRef(null);
    const activeTabRef = useRef(null);
    const inkBarRef = useRef(null);
    const [next, setNext] = useState(false);
    const [prev, setPrev] = useState(false);
    const [prevActiveKey, setActiveKey] = useState(activeKey);
    const getTabs = () => {
        return [...currentPanelMap.entries()].reduce((rst, [key, child], index, list) => {
            const { disabled, closable = true, count, overflowCount, showCount } = child;
            const classes = [`${prefixCls}-tab`];
            const tabProps = {
                tabKey: key,
                role: 'tab',
                'aria-disabled': 'false',
                'aria-selected': 'false',
                style: {
                    marginRight: tabBarGutter && index === list.length - 1 ? 0 : tabBarGutter,
                },
            };
            if (disabled) {
                classes.push(`${prefixCls}-tab-disabled`);
                tabProps['aria-disabled'] = 'true';
            }
            else {
                tabProps.onTabClick = handleTabClick;
            }
            if (activeKey === key) {
                classes.push(`${prefixCls}-tab-active`);
                tabProps.ref = activeTabRef;
                tabProps['aria-selected'] = 'true';
            }
            tabProps.className = classes.join(' ');
            warning('tab' in child || 'title' in child, 'There must be `tab` or `title` property on children of Tabs.');
            const title = (React.createElement(React.Fragment, null,
                getHeader(child),
                showCount && React.createElement(Count, { prefixCls: prefixCls, count: count, overflowCount: overflowCount })));
            rst.push(React.createElement(Ripple, { disabled: disabled, key: key },
                React.createElement(TabBarInner, Object.assign({}, tabProps), type === TabsType['editable-card'] ? (React.createElement("div", { className: closable ? undefined : `${prefixCls}-tab-unclosable` },
                    title,
                    closable && React.createElement(Icon, { type: "close", onClick: e => onRemoveTab(key, e) }))) : title)));
            return rst;
        }, []);
    };
    const getContent = (contents) => {
        if (extraContent || customizable) {
            return [
                contents,
                React.createElement("div", { key: "extra", className: `${prefixCls}-extra-content` },
                    customizable && (React.createElement(Button, { className: `${prefixCls}-hover-button`, funcType: FuncType.flat, icon: "predefine", size: Size.small, onClick: openCustomizationModal })),
                    extraContent),
            ];
        }
        return [contents];
    };
    const getGroupNode = () => {
        if (groupedPanelsMap.size > Number(hideOnlyGroup)) {
            return (React.createElement(Menu, { prefixCls: `${prefixCls}-group`, selectedKeys: activeGroupKey ? [activeGroupKey] : [], onSelect: handleGroupSelect, mode: isVertical(tabBarPosition) ? 'vertical' : 'horizontal' }, [...groupedPanelsMap.entries()].map(([key, { group: { tab, disabled, dot } }]) => (React.createElement(MenuItem, { key: key, disabled: disabled },
                React.createElement(Badge, { dot: dot }, tab))))));
        }
        return undefined;
    };
    const getInkBarNode = () => {
        const inkBarClassName = `${prefixCls}-ink-bar`;
        const classes = classnames(inkBarClassName, inkBarAnimated ?
            `${inkBarClassName}-animated` :
            `${inkBarClassName}-no-animated`);
        return (React.createElement("div", { style: inkBarStyle, className: classes, key: "inkBar", ref: inkBarRef }));
    };
    const getOffsetWH = useCallback((node) => {
        return node[isVertical(tabBarPosition) ? 'offsetHeight' : 'offsetWidth'];
    }, [tabBarPosition]);
    const getScrollWH = useCallback((node) => {
        return node[isVertical(tabBarPosition) ? 'scrollHeight' : 'scrollWidth'];
    }, [tabBarPosition]);
    const getOffsetLT = useCallback((node) => {
        return node.getBoundingClientRect()[isVertical(tabBarPosition) ? 'top' : 'left'];
    }, [tabBarPosition]);
    const setOffset = useCallback((offset, callback) => {
        const nav = navRef.current;
        if (nav) {
            const target = Math.min(0, offset);
            if (offsetRef.current !== target) {
                offsetRef.current = target;
                const navOffset = {};
                const navStyle = nav.style;
                const transformSupported = isTransformSupported(navStyle);
                if (isVertical(tabBarPosition)) {
                    if (transformSupported) {
                        navOffset.value = `translate3d(0,${target}px,0)`;
                    }
                    else {
                        navOffset.name = 'top';
                        navOffset.value = `${target}px`;
                    }
                }
                else if (transformSupported) {
                    navOffset.value = `translate3d(${target}px,0,0)`;
                }
                else {
                    navOffset.name = 'left';
                    navOffset.value = `${target}px`;
                }
                if (transformSupported) {
                    setTransform(navStyle, navOffset.value);
                }
                else if (navOffset.name) {
                    navStyle[navOffset.name] = navOffset.value;
                }
                if (callback) {
                    callback();
                }
            }
        }
    }, [offsetRef, navRef, tabBarPosition]);
    const setNextPrev = useCallback(() => {
        const navNode = navRef.current;
        const container = containerRef.current;
        const navWrap = navWrapRef.current;
        if (navNode && container && navWrap) {
            const navNodeWH = getScrollWH(navNode);
            const containerWH = getOffsetWH(container);
            const navWrapNodeWH = getOffsetWH(navWrap);
            let offset = offsetRef.current;
            // 当容器小于tab的时候使用最小值才可以防止回弹问题。
            const navNodeWHValue = Math.min(containerWH, navWrapNodeWH);
            const minOffset = navNodeWHValue - navNodeWH; // -165
            let $next = next;
            let $prev = prev;
            if (minOffset >= 0) {
                $next = false;
                setOffset(0);
                offset = 0;
            }
            else if (minOffset < offset) {
                $next = true;
            }
            else {
                $next = false;
                // Test with container offset which is stable
                // and set the offset of the nav wrap node
                const realOffset = navWrapNodeWH - navNodeWH;
                setOffset(realOffset);
                offset = realOffset;
            }
            if (offset < 0) {
                $prev = true;
            }
            else {
                $prev = false;
            }
            if (prev !== $prev) {
                setPrev($prev);
            }
            if (next !== $next) {
                setNext($next);
            }
            return {
                next: $next,
                prev: $prev,
            };
        }
    }, [next, prev, navRef, containerRef, navWrapRef, offsetRef, getScrollWH, getOffsetWH, setOffset]);
    const isNextPrevShown = useCallback((state) => {
        if (state) {
            return state.next || state.prev;
        }
        return next || prev;
    }, [next, prev]);
    const toPrev = useCallback((e) => {
        onPrevClick(e);
        const navWrapNode = navWrapRef.current;
        if (navWrapNode) {
            const navWrapNodeWH = getOffsetWH(navWrapNode);
            setOffset(offsetRef.current + navWrapNodeWH, setNextPrev);
        }
    }, [getOffsetWH, setOffset, navWrapRef, onPrevClick, setNextPrev]);
    const toNext = useCallback((e) => {
        onNextClick(e);
        const navWrapNode = navWrapRef.current;
        if (navWrapNode) {
            const navWrapNodeWH = getOffsetWH(navWrapNode);
            const offset = offsetRef.current;
            setOffset(offset - navWrapNodeWH, setNextPrev);
        }
    }, [getOffsetWH, setOffset, navWrapRef, onNextClick, setNextPrev]);
    const scrollToActiveTab = useCallback((e) => {
        const activeTab = activeTabRef.current;
        const navWrap = navWrapRef.current;
        if (e && e.target !== e.currentTarget || !activeTab || !navWrap) {
            return;
        }
        // when not scrollable or enter scrollable first time, don't emit scrolling
        const needToSroll = isNextPrevShown() && lastNextPrevShownRef.current;
        lastNextPrevShownRef.current = isNextPrevShown();
        if (!needToSroll) {
            return;
        }
        const activeTabWH = getScrollWH(activeTab);
        const navWrapNodeWH = getOffsetWH(navWrap);
        let offset = offsetRef.current;
        const wrapOffset = getOffsetLT(navWrap);
        const activeTabOffset = getOffsetLT(activeTab);
        if (wrapOffset > activeTabOffset) {
            offset += (wrapOffset - activeTabOffset);
            setOffset(offset, setNextPrev);
        }
        else if ((wrapOffset + navWrapNodeWH) < (activeTabOffset + activeTabWH)) {
            offset -= (activeTabOffset + activeTabWH) - (wrapOffset + navWrapNodeWH);
            setOffset(offset, setNextPrev);
        }
    }, [activeTabRef, navWrapRef, lastNextPrevShownRef, getScrollWH, getOffsetWH, getOffsetLT, setOffset, setNextPrev, isNextPrevShown]);
    const prevTransitionEnd = useCallback((e) => {
        if (e.propertyName !== 'opacity') {
            return;
        }
        const { current } = containerRef;
        if (current) {
            scrollToActiveTab({
                target: current,
                currentTarget: current,
            });
        }
    }, [scrollToActiveTab, containerRef]);
    const getScrollBarNode = (content) => {
        const showNextPrev = prev || next;
        const prevButton = (React.createElement("span", { onClick: prev ? toPrev : undefined, unselectable: "on", className: classnames(`${prefixCls}-tab-prev`, {
                [`${prefixCls}-tab-btn-disabled`]: !prev,
                [`${prefixCls}-tab-arrow-show`]: showNextPrev,
            }), onTransitionEnd: prevTransitionEnd },
            React.createElement("span", { className: `${prefixCls}-tab-prev-icon` })));
        const nextButton = (React.createElement("span", { onClick: next ? toNext : undefined, unselectable: "on", className: classnames({
                [`${prefixCls}-tab-next`]: 1,
                [`${prefixCls}-tab-btn-disabled`]: !next,
                [`${prefixCls}-tab-arrow-show`]: showNextPrev,
            }) },
            React.createElement("span", { className: `${prefixCls}-tab-next-icon` })));
        const navClassName = `${prefixCls}-nav`;
        const navClasses = classnames(navClassName, scrollAnimated ? `${navClassName}-animated` : `${navClassName}-no-animated`);
        return (React.createElement("div", { className: classnames({
                [`${prefixCls}-nav-container`]: 1,
                [`${prefixCls}-nav-container-scrolling`]: showNextPrev,
            }), key: "container", ref: containerRef },
            prevButton,
            nextButton,
            React.createElement("div", { className: `${prefixCls}-nav-wrap`, ref: navWrapRef },
                React.createElement("div", { className: `${prefixCls}-nav-scroll` },
                    React.createElement("div", { className: navClasses, ref: navRef }, content)))));
    };
    useLayoutEffect(() => {
        const inkBarNode = inkBarRef.current;
        if (inkBarNode) {
            const inkBarNodeStyle = inkBarNode.style;
            const activeTab = activeTabRef.current;
            const rootNode = rootRef.current;
            if (activeTab && rootNode) {
                const wrapNode = navRef.current || rootNode;
                const transformSupported = isTransformSupported(inkBarNodeStyle);
                if (!isVertical(tabBarPosition)) {
                    let left = getLeft(activeTab, wrapNode);
                    let width = activeTab.offsetWidth;
                    // If tabNode'width width equal to wrapNode'width when tabBarPosition is top or bottom
                    // It means no css working, then ink bar should not have width until css is loaded
                    if (width === rootNode.offsetWidth) {
                        width = 0;
                    }
                    else if (inkBarStyle && inkBarStyle.width !== undefined) {
                        width = parseFloat(inkBarStyle.width);
                        if (width) {
                            left += (activeTab.offsetWidth - width) / 2;
                        }
                    }
                    // use 3d gpu to optimize render
                    if (transformSupported) {
                        setTransform(inkBarNodeStyle, `translate3d(${left}px,0,0)`);
                        inkBarNodeStyle.width = `${width}px`;
                        inkBarNodeStyle.height = '';
                    }
                    else {
                        inkBarNodeStyle.left = `${left}px`;
                        inkBarNodeStyle.top = '';
                        inkBarNodeStyle.bottom = '';
                        inkBarNodeStyle.right = `${wrapNode.offsetWidth - left - width}px`;
                    }
                }
                else {
                    let top = getTop(activeTab, wrapNode);
                    let height = activeTab.offsetHeight;
                    if (inkBarStyle && inkBarStyle.height !== undefined) {
                        height = parseFloat(inkBarStyle.height);
                        if (height) {
                            top += (activeTab.offsetHeight - height) / 2;
                        }
                    }
                    if (transformSupported) {
                        setTransform(inkBarNodeStyle, `translate3d(0,${top}px,0)`);
                        inkBarNodeStyle.height = `${height}px`;
                        inkBarNodeStyle.width = '';
                    }
                    else {
                        inkBarNodeStyle.left = '';
                        inkBarNodeStyle.right = '';
                        inkBarNodeStyle.top = `${top}px`;
                        inkBarNodeStyle.bottom = `${wrapNode.offsetHeight - top - height}px`;
                    }
                }
            }
            inkBarNodeStyle.visibility = activeTab ? 'visible' : 'hidden';
        }
    });
    useEffect(() => {
        setOffset(0, setNextPrev);
    }, [tabBarPosition]);
    useLayoutEffect(() => {
        const currentNextPrev = {
            prev, next,
        };
        const nextPrev = setNextPrev();
        // wait next, prev show hide
        /* eslint react/no-did-update-set-state:0 */
        if (isNextPrevShown(currentNextPrev) !== isNextPrevShown(nextPrev)) {
            scrollToActiveTab();
        }
        else if (activeKey !== prevActiveKey) {
            setActiveKey(activeKey);
            // can not use props.activeKey
            scrollToActiveTab();
        }
    }, [setNextPrev, isNextPrevShown, prev, next, activeKey, prevActiveKey]);
    useEffect(() => {
        const debouncedResize = debounce(() => {
            setNextPrev();
            scrollToActiveTab();
        }, 200);
        resizeEvent.addEventListener('resize', debouncedResize);
        return () => {
            resizeEvent.removeEventListener('resize', debouncedResize);
            debouncedResize.cancel();
        };
    }, [setNextPrev, scrollToActiveTab, resizeEvent]);
    const inkBarNode = getInkBarNode();
    const tabs = getTabs();
    const groupNode = getGroupNode();
    const scrollbarNode = getScrollBarNode([inkBarNode, tabs]);
    return (React.createElement("div", Object.assign({ role: "tablist", className: classnames(`${prefixCls}-bar`, { [`${prefixCls}-bar-with-groups`]: groupNode }, className), tabIndex: 0, ref: rootRef, onKeyDown: handleKeyDown, style: style }, getDataAttr(restProps)),
        React.createElement("div", { className: `${prefixCls}-bar-inner` },
            groupNode,
            groupNode && React.createElement("div", { className: `${prefixCls}-bar-divider` }),
            getContent(scrollbarNode))));
};
TabBar.displayName = 'TabBar';
TabBar.defaultProps = {
    inkBarAnimated: true,
    scrollAnimated: true,
};
export default TabBar;
